import { Injectable } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { OutstandingSurveyResponse } from "@common/ADAPT.Common.Model/organisation/outstanding-survey-response";
import { Survey, SurveyBreezeModel, SurveyStatus, SurveyType } from "@common/ADAPT.Common.Model/organisation/survey";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { UserService } from "@common/user/user.service";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { ActivityType, CulturalActivityMetadataByType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { PersonService } from "@org-common/lib/person/person.service";
import { SurveyService } from "@org-common/lib/survey/survey.service";
import { lastValueFrom, merge, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

@Injectable()
export class CulturalActivityHandler extends ActivityHandler<CulturalActivity> {
    public readonly entityType = CulturalActivity;
    public readonly activityTypes = [
        CulturalActivityType.CareerValuation,
        CulturalActivityType.CulturalIndex,
        CulturalActivityType.PeerCatchUp,
        CulturalActivityType.TeamAssessment,
        CulturalActivityType.ResilientBusinessAssessment,
        CulturalActivityType.EmployeeEngagementAssessment,
    ];

    constructor(
        private commonDataService: CommonDataService,
        private culturalActivityService: CulturalActivityService,
        private surveyService: SurveyService,
        private userService: UserService,
        private personService: PersonService,
        private rxjsBreezeService: RxjsBreezeService,
        private authorisationService: AuthorisationService,
    ) {
        super();
    }

    public get shouldUpdate$() {
        return merge(
            this.rxjsBreezeService.entityTypeChanged(CulturalActivity).pipe(
                map(({ type }) => [type]),
            ),
            // update the activities when an osr changes that involves the current person
            this.rxjsBreezeService.entityTypeChanged(OutstandingSurveyResponse).pipe(
                // need to use the connectionId as osr when submitted will be detached. cannot use nav prop for connection.personId
                filter((osr) => this.userService.currentPerson?.connections
                    ?.map((c) => c.connectionId)
                    ?.includes(osr.connectionId) ?? false),
                switchMap((osr) => osr.survey
                    ? of(osr.survey)
                    : this.surveyService.getSurveyById(osr.surveyId)),
                filter((survey) => survey?.surveyType !== SurveyType.OrganisationDiagnostic),
                map((survey) => survey
                    ? [ActivitiesService.getActivityTypeForSurvey(survey)]
                    // no survey, just refresh all survey types since we can't know what the type of the deleted survey is
                    : [CulturalActivityType.TeamAssessment, CulturalActivityType.ResilientBusinessAssessment, CulturalActivityType.EmployeeEngagementAssessment]),
            ),
            this.rxjsBreezeService.entityTypeChanged(Survey).pipe(
                filter((survey) => survey.surveyType !== SurveyType.OrganisationDiagnostic),
                map((survey) => [ActivitiesService.getActivityTypeForSurvey(survey)]),
            ),
        );
    }

    public async getGroupedActivities(personId: number, startDate?: Date, endDate?: Date, excludeCompleted?: boolean, typesToUpdate?: ActivityType[]) {
        typesToUpdate = typesToUpdate ?? this.activityTypes;

        const groups = ActivitiesService.generateActivitiesGroupedByTypeObject(typesToUpdate);

        const culturalActivities = await lastValueFrom(this.culturalActivityService.getCulturalActivitiesForPerson(personId, startDate, endDate, excludeCompleted, typesToUpdate as CulturalActivityType[]));

        // if there are some surveys in the response, we have to fetch the outstanding survey responses
        let outstandingResponses: OutstandingSurveyResponse[] = [];
        let surveys: Survey[] = [];
        const surveyActivities = culturalActivities.filter((e) => e.extensions.isSurvey && e.entityId);
        if (surveyActivities.length > 0) {
            outstandingResponses = await lastValueFrom(this.surveyService.getOutstandingSurveyResponsesForPerson(false, personId));

            const surveyIds = surveyActivities.map((e) => e.entityId!);

            // check which surveys we have cached already so we don't need to fetch them again
            const cachedSurveys = surveyIds
                .map((surveyId) => this.commonDataService.getLocalModelEntityById(SurveyBreezeModel, surveyId))
                .filter((survey) => !!survey);
            const toFetchSurveyIds = surveyIds.filter((surveyId) => !cachedSurveys.find((survey) => survey.surveyId === surveyId));

            // fetch all the surveys mentioned as activity-card will need to fetch them individually otherwise
            const fetchedSurveys = toFetchSurveyIds.length > 0
                ? await lastValueFrom(this.surveyService.getSurveysByIds(toFetchSurveyIds))
                : [];
            surveys = ArrayUtilities.distinct(fetchedSurveys.concat(cachedSurveys));
        }

        // pre-sort culturalActivities so we don't have to do it later
        for (const activity of culturalActivities.sort(SortUtilities.getSortByFieldFunction("date"))) {
            const group = groups[activity.type];

            group.allItems = group.allItems ?? [];
            group.allItems.push(activity);

            let status = activity.extensions.activityStatus;

            if (activity.extensions.isSurvey && activity.entityId) {
                const outstandingResponse = outstandingResponses.find((osr) => osr.surveyId === activity.entityId);
                const survey = surveys.find((s) => s.surveyId === activity.entityId);
                // don't show surveys you've responded to already
                if (survey?.status === SurveyStatus.Started && !outstandingResponse) {
                    continue;
                }

                // any surveys with an outstanding response are "overdue"
                status = outstandingResponse ? CulturalActivityStatus.Overdue : status;
            }

            if (!activity.isCompleted || !excludeCompleted) {
                // TODO: decide if we need to trim the number of events for display
                group[status].push(activity);
                group.displayedCount += 1;

                group.totalCount += 1;
                group.totalCountByStatus[status] += 1;
            }
        }

        return groups;
    }

    public async getCalendarEventForActivity(activity: CulturalActivity) {
        const type = ActivitiesService.getActivityType(activity);
        const metadata = CulturalActivityMetadataByType[type];
        const currentPersonId = this.userService.currentPerson?.personId;

        const teamPrefix = (activity.team ? `${activity.team?.name}: ` : "");
        let title = teamPrefix + metadata.shortTitle;

        if (activity.type === CulturalActivityType.CareerValuation
            || activity.type === CulturalActivityType.CulturalIndex
            || activity.type === CulturalActivityType.PeerCatchUp) {
            const personId = activity.extensions.getOtherPersonId(currentPersonId);
            if (personId) {
                const person = await lastValueFrom(this.personService.getPerson(personId));
                if (person) {
                    if (activity.type === CulturalActivityType.CareerValuation || activity.type === CulturalActivityType.CulturalIndex) {
                        const isCulturalLeader = await this.authorisationService.promiseToGetHasAccess(CulturalLeadershipFrameworkAuthService.ConfigureCohorts);
                        const currentPersonIsCulturalLeaderForActivity = !!activity.person2Id && activity.person2Id === currentPersonId;
                        const culturalLeaderPrefix = isCulturalLeader ? "My " : "";

                        title = currentPersonIsCulturalLeaderForActivity
                            ? `${teamPrefix}${metadata.shortTitle} for ${person.fullName}`
                            : `${teamPrefix}${culturalLeaderPrefix}${metadata.shortTitle} with ${person.fullName}`;
                    } else {
                        title = `${teamPrefix}${metadata.shortTitle} with ${person.fullName}`;
                    }
                }
            }
        } else if (activity.extensions.isSurvey) {
            if (activity.entityId) {
                const survey = await lastValueFrom(this.culturalActivityService.getEntityForCulturalActivity(activity)) as Survey | undefined;
                if (survey) {
                    // get team name
                    title = survey.surveyType === SurveyType.TeamAssessment
                        ? `${survey.team?.name}: ${survey.name}`
                        : survey.name;
                } else {
                    title = teamPrefix + "Unknown survey";
                }
            } else {
                title = teamPrefix + "Assessment to be scheduled";
            }
        }

        if (activity.isCompleted) {
            const text = activity.extensions.isSurvey ? "Ended" : "Completed";
            title = `(${text}) ${title}`;
        }

        return { title, startDate: activity.date, endDate: undefined, completed: activity.isCompleted };
    }
}
